import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { Close, Diversity3 } from "@mui/icons-material";
import { SubmitHandler, useForm } from "react-hook-form";
import { useState } from "react";
import {
  addClassroom as addClassroomApi,
  editClassroom as editClassroomApi,
} from "apis";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { splitMonths } from "utils";

interface ClassroomItem {
  id: number;
  name: string;
  description: string;
  rangeFrom: number;
  rangeTo: number;
}

interface FormFields {
  name: string;
  description: string;
  rangeFromYears: number;
  rangeFromMonths: number;
  rangeToYears: number;
  rangeToMonths: number;
}

interface Props {
  open: boolean;
  onClose: () => void;
  item?: ClassroomItem;
}

const inputSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

const toMonths = (years: unknown, months: unknown): number =>
  (Number(years) || 0) * 12 + (Number(months) || 0);

const ClassroomDialog: React.FC<Props> = ({ open, onClose, item }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isEdit = !!item;
  const fromParts = item
    ? splitMonths(item.rangeFrom)
    : { years: 0, months: 0 };
  const toParts = item ? splitMonths(item.rangeTo) : { years: 0, months: 0 };

  const { register, handleSubmit } = useForm<FormFields>({
    defaultValues: {
      name: item?.name ?? "",
      description: item?.description ?? "",
      rangeFromYears: fromParts.years,
      rangeFromMonths: fromParts.months,
      rangeToYears: toParts.years,
      rangeToMonths: toParts.months,
    },
  });
  const { enqueueSnackbar } = useSnackbar();
  const [submitting, setSubmitting] = useState(false);

  const onSubmit: SubmitHandler<FormFields> = (data) => {
    const basePayload = {
      name: data.name,
      description: data.description,
      rangeFrom: toMonths(data.rangeFromYears, data.rangeFromMonths),
      rangeTo: toMonths(data.rangeToYears, data.rangeToMonths),
    };
    setSubmitting(true);
    const request = isEdit
      ? editClassroomApi({ ...basePayload, id: item!.id })
      : addClassroomApi(basePayload);
    request
      .then(() => {
        enqueueSnackbar(t(isEdit ? "classroom.updated" : "classroom.added"), {
          variant: "success",
        });
        onClose();
      })
      .catch((err) => {
        enqueueSnackbar(err?.response?.data?.message || "Failed", {
          variant: "error",
        });
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
          color: "common.white",
          px: 3,
          py: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box display="flex" alignItems="center" gap={1.5}>
          <Diversity3 sx={{ fontSize: 22 }} />
          <Typography variant="h6" fontWeight={700}>
            {t(isEdit ? "classroom.edit" : "classroom.add")}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ color: "common.white" }}
        >
          <Close />
        </IconButton>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ px: 3, py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                label={t("inputs.class_name")}
                fullWidth
                size="small"
                {...register("name", { required: true })}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label={t("inputs.description")}
                fullWidth
                size="small"
                multiline
                rows={3}
                {...register("description", { required: true })}
                sx={inputSx}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
              >
                {t("inputs.age_group")}
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t("inputs.range_min")}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  label={t("inputs.years")}
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 0, max: 6 }}
                  {...register("rangeFromYears")}
                  sx={inputSx}
                />
                <TextField
                  label={t("inputs.months")}
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 0, max: 11 }}
                  {...register("rangeFromMonths")}
                  sx={inputSx}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t("inputs.range_max")}
              </Typography>
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  label={t("inputs.years")}
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 0, max: 6 }}
                  {...register("rangeToYears")}
                  sx={inputSx}
                />
                <TextField
                  label={t("inputs.months")}
                  fullWidth
                  size="small"
                  type="number"
                  inputProps={{ min: 0, max: 11 }}
                  {...register("rangeToMonths")}
                  sx={inputSx}
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>

        <Box sx={{ px: 3, pb: 3, display: "flex", gap: 1.5 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            fullWidth
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: "none",
              fontWeight: 600,
            }}
          >
            {t("buttons.cancel")}
          </Button>
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={submitting}
            sx={{
              borderRadius: 2,
              py: 1.2,
              textTransform: "none",
              fontWeight: 600,
              boxShadow: "none",
              "&:hover": { boxShadow: 2 },
            }}
          >
            {submitting ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t(isEdit ? "buttons.save" : "buttons.add")
            )}
          </Button>
        </Box>
      </form>
    </Dialog>
  );
};

export default ClassroomDialog;
